import { Link } from 'react-router-dom'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import type {
  AdminView,
  ParseFailureView,
  SyncStateView,
  SystemConfig,
  SystemStatus,
} from '~/lib/api/types'
import { PersonalStatsStrip } from './personal-stats-strip'
import { useSystemStatus } from './use-system-status'
import styles from './settings-page.module.scss'

const FMT_DATETIME = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

export function SettingsPage() {
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false

  return (
    <div className={styles.page}>
      <header>
        <h1 className={styles.heading}>Settings</h1>
        <p className={styles.subhead}>
          {isAdmin
            ? 'operational status · admin-only'
            : 'your check-in activity'}
        </p>
      </header>

      <PersonalStatsStrip />

      {isAdmin && <AdminSections />}
    </div>
  )
}

function AdminSections() {
  const { data, isLoading, isError, refetch } = useSystemStatus()

  return (
    <>
      {isLoading && (
        <p className={styles.placeholder} aria-live="polite">
          Loading status…
        </p>
      )}

      {isError && (
        <p className={styles.placeholder} role="alert">
          Couldn&apos;t load settings.{' '}
          <button
            type="button"
            className={styles.personLink}
            onClick={() => refetch()}
          >
            Retry →
          </button>
        </p>
      )}

      {data && <Sections status={data} />}
    </>
  )
}

function Sections({ status }: { status: SystemStatus }) {
  return (
    <>
      <SystemSection system={status.system} />
      <SyncSection states={status.sync_states} />
      <ParseFailuresSection failures={status.parse_failures} />
      <AdminsSection admins={status.admins} />
    </>
  )
}

function SystemSection({ system }: { system: SystemConfig }) {
  return (
    <section className={styles.section} aria-labelledby="settings-system">
      <div className={styles.sectionHeader}>
        <h2 id="settings-system" className={styles.sectionTitle}>
          System
        </h2>
        <span className={styles.sectionHint}>read-only · managed via env</span>
      </div>
      <dl className={styles.kvList}>
        <KV
          label="#checkins channel id"
          value={system.checkins_channel_id}
          mono
        />
        <KV
          label="holidays calendar"
          value={system.holidays_calendar_id}
          mono
        />
        <KV
          label="time-off calendar"
          value={system.time_off_calendar_id}
          mono
        />
        <KVFlag
          label="gcal sync"
          on={system.gcal_sync_enabled}
          onLabel="enabled"
        />
        <KVFlag
          label="slack oauth"
          on={system.slack_oauth_configured}
          onLabel="configured"
        />
      </dl>
    </section>
  )
}

function SyncSection({ states }: { states: SyncStateView[] }) {
  return (
    <section className={styles.section} aria-labelledby="settings-sync">
      <div className={styles.sectionHeader}>
        <h2 id="settings-sync" className={styles.sectionTitle}>
          Google Calendar sync
        </h2>
        <span className={styles.sectionHint}>per kind</span>
      </div>
      {states.length === 0 ? (
        <p className={styles.empty}>No sync activity yet.</p>
      ) : (
        <ul className={styles.list}>
          {states.map(s => (
            <li
              key={s.calendar_id}
              className={styles.row}
              aria-label={`${s.kind ?? 'unknown'} sync state`}
            >
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>
                  <span className="mono">{s.kind ?? '—'}</span> ·{' '}
                  {s.calendar_id}
                </span>
                <span className={styles.rowMeta}>
                  {s.last_error ? (
                    <span className={styles.errorText}>
                      last error{' '}
                      {s.last_error_at ? formatTime(s.last_error_at) : ''}:{' '}
                      {s.last_error}
                    </span>
                  ) : (
                    `last synced ${s.last_synced_at ? formatTime(s.last_synced_at) : 'never'}`
                  )}
                </span>
              </div>
              <span className={styles.rowAside}>
                {s.has_sync_token ? 'incremental' : 'full resync next'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ParseFailuresSection({ failures }: { failures: ParseFailureView[] }) {
  return (
    <section className={styles.section} aria-labelledby="settings-failures">
      <div className={styles.sectionHeader}>
        <h2 id="settings-failures" className={styles.sectionTitle}>
          Recent parse failures
        </h2>
        <span className={styles.sectionHint}>{failures.length} latest</span>
      </div>
      {failures.length === 0 ? (
        <p className={styles.empty}>No parse failures.</p>
      ) : (
        <ul className={styles.list}>
          {failures.map(f => (
            <li key={f.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>
                  <Link
                    className={styles.personLink}
                    to={`/people/${encodeURIComponent(f.slack_user_id)}`}
                  >
                    {f.display_name}
                  </Link>
                </span>
                <span className={styles.rowMeta}>
                  <span className="mono">{f.date}</span>
                  {f.submitted_at
                    ? ` · submitted ${formatTime(f.submitted_at)}`
                    : ''}
                </span>
              </div>
              <span className={styles.rowAside}>parse failed</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function AdminsSection({ admins }: { admins: AdminView[] }) {
  return (
    <section className={styles.section} aria-labelledby="settings-admins">
      <div className={styles.sectionHeader}>
        <h2 id="settings-admins" className={styles.sectionTitle}>
          Admins
        </h2>
        <span className={styles.sectionHint}>{admins.length}</span>
      </div>
      {admins.length === 0 ? (
        <p className={styles.empty}>No admins configured.</p>
      ) : (
        <ul className={styles.list}>
          {admins.map(a => (
            <li key={a.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>
                  <Link
                    className={styles.personLink}
                    to={`/people/${encodeURIComponent(a.slack_user_id)}`}
                  >
                    {a.display_name}
                  </Link>
                </span>
                <span className={styles.rowMeta}>{a.email ?? '—'}</span>
              </div>
              <span className={`${styles.rowAside} mono`}>
                {a.slack_user_id}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function KV({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string | null
  mono?: boolean
}) {
  return (
    <>
      <dt className={`${styles.kvKey} mono`}>{label}</dt>
      <dd
        className={
          value ? `${styles.kvValue}${mono ? ' mono' : ''}` : styles.kvMissing
        }
      >
        {value ?? 'not set'}
      </dd>
    </>
  )
}

function KVFlag({
  label,
  on,
  onLabel,
}: {
  label: string
  on: boolean
  onLabel: string
}) {
  return (
    <>
      <dt className={`${styles.kvKey} mono`}>{label}</dt>
      <dd className={on ? styles.statusOn : styles.statusOff}>
        {on ? onLabel : 'disabled'}
      </dd>
    </>
  )
}

function formatTime(iso: string): string {
  try {
    return FMT_DATETIME.format(new Date(iso))
  } catch {
    return iso
  }
}
